import { Box, Container, Typography, useMediaQuery, useTheme } from '@mui/material';

import {
	Maybe,
	ImageAsset,
	MultiCardV2,
	MultiCardV2ButtonsCollection,
	MultiCardV2DescriptionRichText,
	MultiCardV2SimpleCard,
	MultiCardV2SimpleCardTwo,
	MultiCardV2SimpleCardThree,
	MultiCardV2SimpleCardFour,
	ContentTypeRichTextContent as TContentTypeRichTextContent,
} from '@/types/generated';
import getHeadingVariant from '@/utils/resolveContentfulHeadingVariant';
import { ContentfulBackground } from '@/types';
import ContentTypeRichText from '@/components/ContentTypeRichText';
import ContentfulButton from '@/components/ContentfulButton';
import { resolveContentfulBgColor, translateTagColorNameForContentful, textToJumpLink } from '@/utils';
import TemplateContainer from '@/components/TemplateContainer';
import { IImageAsset } from '@/components/ImageAsset/ImageAsset';

import {
	getSectionWrapperStyles,
	getCardsWrapperStyles,
	wrapper,
	getHeadingStyles,
	richTextDescStyles,
	richTextDescStyles2,
	richTextDescStyles3,
	getMultiCardButtonsStyles,
	backgroundContainerStyles,
	getEyebrowStyles,
} from './MultiCard.styles';
export interface IMultiCard extends MultiCardV2 {
	/**
	 * Used to identify a component on a page for the purpose of jumping to it using the anchor link.
	 *
	 * @type {Maybe<string>}
	 * @memberof IMultiCard
	 */
	anchorId?: Maybe<string>;
	/**
	 * Specifies the background color of the template.
	 *
	 * @type {Maybe<TAGBackground>}
	 * @memberof IMultiCard
	 */
	backgroundColor?: Maybe<string>;
	/**
	 * Specifies if the background image should be displayed on big screens.
	 *
	 * @type {Maybe<boolean>}
	 * @memberof IMultiCard
	 * @deprecated not used in MultiCardV2
	 */
	bigScreenBackground?: Maybe<boolean>;
	/**
	 * Template section rich text description
	 *
	 * @type {Maybe<MultiCardV2DescriptionRichText>}
	 * @memberof IMultiCard
	 */
	descriptionRichText?: Maybe<MultiCardV2DescriptionRichText>;
	/**
	 * lastRowCardsAlignment will be applied for more than 3 cards and in desktop mode.
	 *
	 * @type {Maybe<string>}
	 * @memberof IMultiCard
	 * @deprecated not used in MultiCardV2
	 */
	lastRowCardsAlignment?: Maybe<string>;
	/**
	 * Specifies the section eyebrow text.
	 *
	 * @type {Maybe<string>}
	 * @memberof IMultiCard
	 */
	sectionEyebrow?: Maybe<string>;
	/**
	 * Specifies the text alignment for the section info.
	 *
	 * @type {Maybe<flex-start | center>}
	 * @memberof IMultiCard
	 */
	sectionInfoAlignment?: Maybe<string>;
	/**
	 * Specifies the section title text.
	 *
	 * @type {Maybe<string>}
	 * @memberof IMultiCard
	 */
	sectionTitle?: Maybe<string>;
	/**
	 * Optional setting for choosing the heading type.
	 *
	 * @type {Maybe<HeadingTag>}
	 * @memberof IMultiCard
	 */
	//sectionTitleHtag?: Maybe<HeadingTag>;
	/**
	 * Optional data-testid attribute for testing purposes.
	 *
	 * @type {string}
	 * @memberof IMultiCard
	 */
	dataTestId?: string;
	/**
	 * The collection of buttons to display at the bottom of component.
	 *
	 * @type {Maybe<MultiCardV2ButtonsCollection>}
	 * @memberof IMultiCard
	 */
	buttonsCollection?: Maybe<MultiCardV2ButtonsCollection>;
	/**
	 * The image to display. The reference to an image asset in Contentful.
	 * @type {Maybe<ImageAsset>}
	 * @memberof IMultiCard
	 */
	backgroundImage?: Maybe<ImageAsset>;

	/**
	 * The simple card component to display in the section.
	 *
	 * @type {Maybe<MultiCardV2SimpleCard>}
	 * @memberof IMultiCard
	 */
	simpleCard?: Maybe<MultiCardV2SimpleCard>;

	/**
	 * The second simple card component to display in the section.
	 *
	 * @type {Maybe<MultiCardV2SimpleCardTwo>}
	 * @memberof IMultiCard
	 */
	simpleCardTwo?: Maybe<MultiCardV2SimpleCardTwo>;

	/**
	 * The third simple card component to display in the section.
	 *
	 * @type {Maybe<MultiCardV2SimpleCardThree>}
	 * @memberof IMultiCard
	 */
	simpleCardThree?: Maybe<MultiCardV2SimpleCardThree>;
	/**
	 * The fourth simple card component to display in the section.
	 *
	 * @type {Maybe<MultiCardV2SimpleCardFour>}
	 * @memberof IMultiCard
	 */
	simpleCardFour?: Maybe<MultiCardV2SimpleCardFour>;
	/**
	 * Specifies the variant of which componenet we want.
	 *
	 * @type {Maybe<string>}
	 * @memberof IMultiCard
	 */
	variant?: Maybe<string>;
}

export default function MultiCard({
	anchorId,
	dataTestId = 'section_multi_card',
	backgroundColor,
	sectionEyebrow,
	sectionTitle,
	descriptionRichText,
	sectionInfoAlignment,
	buttonsCollection,
	backgroundImage,
	fixBackgroundToBottom = false,
	simpleCard,
	simpleCardTwo,
	simpleCardThree,
	simpleCardFour,
	variant,
}: Readonly<IMultiCard>) {
	const cardData = [
		...(simpleCard ? [{ body: simpleCard }] : []),
		...(simpleCardTwo ? [{ body: simpleCardTwo }] : []),
		...(simpleCardThree ? [{ body: simpleCardThree }] : []),
		...(simpleCardFour ? [{ body: simpleCardFour }] : []),
	];

	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md')); // < 900px

	// solve issue with variant naming structure ie. lightGradient vs Light Gradient
	const contentfulSafeBackgroundColor = translateTagColorNameForContentful(backgroundColor || '') || '';
	const contentfulBackgroundColor = resolveContentfulBgColor(
		contentfulSafeBackgroundColor as ContentfulBackground,
		theme
	);
	const isDarkBackground = !!backgroundColor?.toLowerCase().includes('dark');

	const bottomButtons = buttonsCollection?.items;

	return (
		<TemplateContainer
			maxWidth={false}
			containerSx={{ ...wrapper }}
			dataTestId={dataTestId}
			contentfulBackgroundColor={contentfulBackgroundColor}
			{...(anchorId && { id: textToJumpLink(anchorId) })}
		>
			<Box
				sx={{
					...backgroundContainerStyles(backgroundImage as IImageAsset, fixBackgroundToBottom, isSmallScreen),
				}}
			>
				<Container sx={getSectionWrapperStyles(isSmallScreen, sectionInfoAlignment)}>
					{sectionEyebrow && (
						<>
							<Typography
								sx={getEyebrowStyles(theme)}
								variant={isSmallScreen ? 'bodySmallBook' : 'bodyMediumBook'}
								color={isDarkBackground ? 'text.light' : 'text.secondary'}
								data-testid="text_multicard_section_eyebrow"
							>
								{sectionEyebrow}
							</Typography>
							{isSmallScreen && variant === 'TeamTag' ? (
								<Box width={'100%'} height={'1px'} bgcolor={'#FFF'}></Box>
							) : null}
						</>
					)}

					{sectionTitle && (
						<Typography
							variant={getHeadingVariant('h1')}
							component={'h1'}
							sx={{
								...getHeadingStyles(theme),
								color: isDarkBackground ? 'text.light' : 'text.primary',
							}}
							whiteSpace="pre-line"
							data-testid="text_multicard_section_title"
						>
							{sectionTitle}
						</Typography>
					)}

					{descriptionRichText ? (
						<ContentTypeRichText
							content={descriptionRichText as TContentTypeRichTextContent}
							dataTestId={`text_multicard_section_description_rich_text`}
							disableGutters
							backgroundColor="transparent"
							disablePaddingOn={['Top', 'Bottom']}
							contentMaxWidth="100%"
							docProps={{
								color: isDarkBackground ? 'text.light' : 'text.secondary',
								variant: isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook',
								textAlign: !isSmallScreen ? 'center' : 'left',
								fontWeight: 350,
							}}
							containerSx={richTextDescStyles2(isSmallScreen)}
						/>
					) : null}
				</Container>

				<Container
					disableGutters
					sx={getCardsWrapperStyles({
						numberOfCards: cardData.length,
						isSmallScreen,
					})}
					data-testid="group_multicards_cards"
				>
					{cardData?.map((cards, index) => (
						<Box key={index} textAlign="center" color="white" py={isSmallScreen ? 0 : '24px'}>
							<ContentTypeRichText
								content={cards.body as TContentTypeRichTextContent}
								dataTestId={`text_multicard_section_description_rich_text_${index}`}
								disableGutters
								backgroundColor="transparent"
								disablePaddingOn={['Top', 'Bottom']}
								contentMaxWidth="100%"
								docProps={{
									variant: isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook',
									textAlign: !isSmallScreen ? 'center' : 'left',
									fontWeight: 350,
								}}
								containerSx={
									variant === 'TeamTag'
										? richTextDescStyles3(isSmallScreen)
										: richTextDescStyles(isSmallScreen)
								}
							/>
						</Box>
					))}
				</Container>

				{bottomButtons && bottomButtons.length > 0 ? (
					<Box sx={getMultiCardButtonsStyles(isSmallScreen)} data-testid="multicard_buttons_box">
						{bottomButtons.map((button) =>
							button ? (
								<ContentfulButton
									key={button?.sys?.id}
									{...button}
									dataTestId={`multicard_button_${button?.name || ''}`}
									docProps={{
										border: '1px solid #FFFF',
										marginTop: isSmallScreen ? '2.5rem' : 0,
										gap: '0.625rem',
										fontSize: '0.875rem',
										fontWeight: 700,
										lineHeight: '1.25rem',
										letterSpacing: '0.188rem',
										color: '#FFFF',
										fontFamily: 'Messina Sans',
									}}
								/>
							) : null
						)}
					</Box>
				) : null}
			</Box>
		</TemplateContainer>
	);
}
